import { css } from '@emotion/core'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { usePageView } from '@fs/zion-analytics'
import { useOverlay, DataBlock, ResponsiveTableRow, Row, H5, DangerButton } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { ContentDelete } from '@fs/zion-icon/dist/cjs/icons'
import { DmcResponsiveCheckboxTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import { PersonContext } from '../PersonContext'
import { UserLink } from '../../app/Links'
import OtherInfoDeleteDialog from './OtherInfoDeleteDialog'
import useOtherInfo from '../../../hooks/useOtherInfo'
import { deepCopy } from '../../../util/Utils'
import { GENEALOGICAL_DATE_FORMAT, dateStringToTimestamp, utcToLocaleString } from '../../../util/DateUtil'

const floatRight = css`
  float: right;
`

export default function OtherInfo() {
  usePageView({ page_detail: 'DMConsole:OtherInfo' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.person.otherInfo'))
  const { currentPerson } = useContext(PersonContext)
  const personId = currentPerson.id
  const { otherInfo } = useOtherInfo(personId)
  const [selectedConclusions, setSelectedConclusions] = useState([])
  const [selectedFactConclusions, setSelectedFactConclusions] = useState([])
  const [selectedNameConclusions, setSelectedNameConclusions] = useState([])
  const [singleDeleting, setSingleDeleting] = useState(false)
  const deleteSelectedDialogOpener = useOverlay()

  const allConclusions = useMemo(() => (otherInfo ? otherInfo.facts.concat(otherInfo.alternateNames) : []), [otherInfo])

  const singleDelete = (individualConclusionId) => {
    setSingleDeleting(true)
    setSelectedConclusions(individualConclusionId)
    deleteSelectedDialogOpener.open()
  }

  useEffect(() => {
    if (!deleteSelectedDialogOpener.isOpen && !deleteSelectedDialogOpener.transitioning) {
      setSingleDeleting(false)
    }
  }, [deleteSelectedDialogOpener.isOpen, deleteSelectedDialogOpener.transitioning])

  useEffect(() => {
    if (allConclusions.length !== 0) {
      if (!singleDeleting) {
        let selectedFactConclusionsCopy = deepCopy(selectedConclusions)
        const possibleSelectedConclusions = allConclusions.map((fact) => fact.conclusionId)
        possibleSelectedConclusions.forEach((id) => {
          if (selectedFactConclusions.includes(id)) {
            if (!selectedFactConclusionsCopy.includes(id)) {
              selectedFactConclusionsCopy.push(id)
              setSelectedConclusions(selectedFactConclusionsCopy)
            }
          } else if (selectedNameConclusions.includes(id)) {
            if (!selectedFactConclusionsCopy.includes(id)) {
              selectedFactConclusionsCopy.push(id)
              setSelectedConclusions(selectedFactConclusionsCopy)
            }
          } else if (selectedFactConclusionsCopy.includes(id)) {
            selectedFactConclusionsCopy = selectedFactConclusionsCopy.filter(
              (idToRemoveCandidate) => id !== idToRemoveCandidate
            )
            setSelectedConclusions(selectedFactConclusionsCopy)
          }
        })
      }
    }
  }, [allConclusions, selectedConclusions, selectedFactConclusions, selectedNameConclusions, singleDeleting])

  if (!otherInfo) {
    return null
  }
  return (
    <>
      {allConclusions.length === 0 ? (
        t('person.other.noInfo', 'This person does not have other information.')
      ) : (
        <>
          <OtherInfoDeleteDialog conclusionIds={selectedConclusions} {...deleteSelectedDialogOpener} />
          {otherInfo.facts.length > 0 && (
            <>
              <Row>
                <H5 growX supplementary>
                  {t('facts', 'Facts')}
                </H5>
                <DangerButton
                  title="Remove Selected Info"
                  Icon={ContentDelete}
                  onClick={deleteSelectedDialogOpener.handleClick}
                  disabled={selectedConclusions.length === 0 || singleDeleting}
                >
                  {t('action.deleteSelected', 'Delete Selected')}
                </DangerButton>
              </Row>
              <DmcResponsiveCheckboxTable
                id="otherInfoTable"
                totalColumnCount={5}
                onSelectionChange={setSelectedFactConclusions}
                atSizeColumns={{
                  default: {
                    labels: [
                      t('type', 'Type'),
                      t('place', 'Place'),
                      t('date', 'Date'),
                      t('attribution.label', 'Attribution'),
                      '',
                    ],
                    columnWidths: [2, 4, 2, 2, 2],
                  },
                }}
              >
                {otherInfo.facts.map((fact, index) => {
                  const individualConclusionId = [fact.conclusionId]
                  const lastestModified = fact.attribution.modifiedDateNormalized.length - 1

                  const trimType = (valueType) => {
                    if (valueType === 'USER_DEFINED') {
                      const userDefined = fact.value.userDefinedType
                      const trimmedUserDefined = userDefined.toString().split(/[\s,]/)
                      return `${t('tf.factType.USER_DEFINED', 'User Defined')} - ${decodeURIComponent(
                        trimmedUserDefined.pop()
                      )}`
                    }
                    const lastElement = valueType.toString().split(/[\s/]/)
                    const factType = lastElement.pop()
                    return t(`tf.factType.${factType}`, factType)
                  }

                  return (
                    <ResponsiveTableRow
                      key={fact.conclusionId}
                      name={fact.conclusionId}
                      column1={<DataBlock heading={trimType(fact.value.type)} />}
                      column2={
                        <>
                          {fact.value && fact.value.place && (
                            <>
                              {fact.value.place.original && (
                                <DataBlock heading={t('original', 'Original')} data={fact.value.place.original} />
                              )}
                              {fact.value.place.names &&
                                fact.value.place.names.map((name) => (
                                  <DataBlock
                                    key={name.lang}
                                    heading={t('name', 'Name')}
                                    data={
                                      <>
                                        ({name.lang}) {name.value}
                                      </>
                                    }
                                  />
                                ))}
                            </>
                          )}
                        </>
                      }
                      column3={
                        <>
                          {fact.value && fact.value.date && (
                            <>
                              <DataBlock heading={t('original', 'Original')} data={fact.value.date.original} />
                              <DataBlock heading={t('person.other.formal', 'Formal')} data={fact.value.date.formal} />
                            </>
                          )}
                        </>
                      }
                      column4={
                        <>
                          {fact.value && fact.value.date && (
                            <>
                              <DataBlock
                                heading={t('contributor', 'Contributor')}
                                data={
                                  <UserLink
                                    id={`contributorLink_${index}`}
                                    userName={fact.attribution.contributor.id}
                                    userId={fact.attribution.contributor.id}
                                  />
                                }
                              />
                              <DataBlock
                                heading={t('submitter', 'Submitter')}
                                data={
                                  <UserLink
                                    id={`submitterLink_${index}`}
                                    userName={fact.attribution.submitter.id}
                                    userId={fact.attribution.submitter.id}
                                  />
                                }
                              />
                              {fact.attribution.changeMessage && (
                                <DataBlock
                                  heading={t('changeMessage', 'Change Message')}
                                  data={fact.attribution.changeMessage}
                                />
                              )}
                              <DataBlock
                                heading={t('modified.label', 'Modified')}
                                data={utcToLocaleString(
                                  dateStringToTimestamp(
                                    fact.attribution.modifiedDateNormalized[lastestModified].value,
                                    GENEALOGICAL_DATE_FORMAT
                                  )
                                )}
                              />
                            </>
                          )}
                        </>
                      }
                      column5={
                        <span css={floatRight}>
                          <DangerButton
                            title="Remove Info"
                            Icon={ContentDelete}
                            onClick={() => singleDelete(individualConclusionId)}
                          >
                            {t('action.delete', 'Delete')}
                          </DangerButton>
                        </span>
                      }
                    />
                  )
                })}
              </DmcResponsiveCheckboxTable>
            </>
          )}
          {otherInfo.alternateNames.length > 0 && (
            <>
              <Row>
                <H5 growX supplementary>
                  {t('person.other.names', 'Names')}
                </H5>
                {otherInfo.facts.length === 0 && (
                  <DangerButton
                    title="Remove Selected Info"
                    Icon={ContentDelete}
                    onClick={deleteSelectedDialogOpener.open}
                    disabled={selectedConclusions.length === 0 || singleDeleting}
                  >
                    {t('action.deleteSelected', 'Delete Selected')}
                  </DangerButton>
                )}
              </Row>
              <DmcResponsiveCheckboxTable
                totalColumnCount={5}
                onSelectionChange={setSelectedNameConclusions}
                atSizeColumns={{
                  default: {
                    labels: [t('type', 'Type'), t('name', 'Name'), t('attribution.label', 'Attribution'), ''],
                    columnWidths: [2, 4, 4, 2],
                  },
                }}
              >
                {otherInfo.alternateNames.map((name) => {
                  const individualConclusionId = [name.conclusionId]
                  const lastestModified = name.attribution.modifiedDateNormalized.length - 1

                  const trimType = (valueType) => {
                    if (valueType === 'USER_DEFINED') {
                      const userDefined = name.value.userDefinedType
                      const trimedUserDefined = userDefined.toString().split(/[\s,]/)
                      return `${t('tf.factType.USER_DEFINED', 'User Defined')} - ${trimedUserDefined.pop()}`
                    }
                    const lastElement = valueType.toString().split(/[\s/]/)
                    const nameType = lastElement.pop()
                    return t(`tf.nameType.${nameType}`, nameType)
                  }

                  return (
                    <ResponsiveTableRow
                      key={name.conclusionId}
                      name={name.conclusionId}
                      column1={<DataBlock heading={trimType(name.value.type)} />}
                      column2={name.value.nameForms[0].fullText}
                      column3={
                        <>
                          <DataBlock
                            heading={t('contributor', 'Contributor')}
                            data={
                              <UserLink
                                userName={name.attribution.contributor.id}
                                userId={name.attribution.contributor.id}
                              />
                            }
                          />
                          <DataBlock
                            heading={t('submitter', 'Submitter')}
                            data={
                              <UserLink
                                userName={name.attribution.submitter.id}
                                userId={name.attribution.submitter.id}
                              />
                            }
                          />
                          {name.attribution.changeMessage && (
                            <DataBlock
                              heading={t('changeMessage', 'Change Message')}
                              data={name.attribution.changeMessage}
                            />
                          )}
                          <DataBlock
                            heading={t('modified.label', 'Modified')}
                            data={name.attribution.modifiedDateNormalized[lastestModified].value}
                          />
                        </>
                      }
                      column4={
                        <span css={floatRight}>
                          <DangerButton
                            title="Remove Info"
                            Icon={ContentDelete}
                            onClick={() => singleDelete(individualConclusionId)}
                          >
                            {t('action.delete', 'Delete')}
                          </DangerButton>
                        </span>
                      }
                    />
                  )
                })}
              </DmcResponsiveCheckboxTable>
            </>
          )}
        </>
      )}
    </>
  )
}
